/**
 * Every application path is declared here so routing, breadcrumbs, guards,
 * SEO canonicals and API builders share one source of truth.
 */

import { DEFAULT_LOCALE, LOCALES, type Locale } from "./config";

export const ROUTE_PARAMS = {
  lang: "lang",
  workspaceSlug: "workspaceSlug",
  orderId: "orderId",
  productId: "productId",
  memberId: "memberId",
  invoiceId: "invoiceId",
  userId: "userId",
  publicToken: "publicToken",
} as const;

export const PUBLIC_HOME_ROUTE = "/" as const;
export const LOGIN_ROUTE = "/login" as const;
export const DASHBOARD_ROUTE = "/dashboard" as const;
export const DASHBOARD_NEW_WORKSPACE_ROUTE = "/dashboard/workspaces/new" as const;
export const NOT_FOUND_ROUTE = "/404" as const;

export const WORKSPACE_ROOT_SEGMENT = "w" as const;

export const WORKSPACE_SECTIONS = {
  home: "home",
  pos: "pos",
  orders: "orders",
  products: "products",
  logs: "logs",
  users: "users",
  settings: "settings",
} as const;

export type WorkspaceSection = (typeof WORKSPACE_SECTIONS)[keyof typeof WORKSPACE_SECTIONS];

const SECTION_PATHS: Record<WorkspaceSection, string> = {
  home: "",
  pos: "pos",
  orders: "orders",
  products: "products",
  logs: "logs",
  users: "users",
  settings: "settings",
};

export const workspacePath = (workspaceSlug: string): string =>
  `/${WORKSPACE_ROOT_SEGMENT}/${workspaceSlug}`;

export const workspaceSectionPath = (
  workspaceSlug: string,
  section: WorkspaceSection,
): string => {
  const suffix = SECTION_PATHS[section];
  return suffix ? `${workspacePath(workspaceSlug)}/${suffix}` : workspacePath(workspaceSlug);
};

export const workspaceOrderPath = (workspaceSlug: string, orderId: string): string =>
  `${workspacePath(workspaceSlug)}/orders/${orderId}`;

export const workspaceInvoicePrintPath = (
  workspaceSlug: string,
  invoiceId: string,
): string => `${workspacePath(workspaceSlug)}/invoices/${invoiceId}/print`;

export const SUPERBOARD_ROOT_SEGMENT = "superboard" as const;
export const SUPERBOARD_ROUTE = `/${SUPERBOARD_ROOT_SEGMENT}` as const;
export const SUPERBOARD_LOGIN_ROUTE = `/${SUPERBOARD_ROOT_SEGMENT}/login` as const;

export const SUPERBOARD_SECTIONS = {
  overview: "overview",
  users: "users",
  workspaces: "workspaces",
  settings: "settings",
} as const;

export type SuperboardSection = (typeof SUPERBOARD_SECTIONS)[keyof typeof SUPERBOARD_SECTIONS];

export const superboardPath = (section?: SuperboardSection): string =>
  section ? `${SUPERBOARD_ROUTE}/${section}` : SUPERBOARD_ROUTE;

/** `/en` | `/id` — public branding is always locale-prefixed. */
export const publicLocalePath = (locale: Locale): string => `/${locale}`;

export const publicHomePath = (locale: Locale = DEFAULT_LOCALE): string =>
  `${publicLocalePath(locale)}/`;

/** Router patterns (react-router syntax). */
export const ROUTE_PATTERNS = {
  publicLocale: `/:${ROUTE_PARAMS.lang}`,
  login: LOGIN_ROUTE,
  dashboard: DASHBOARD_ROUTE,
  dashboardNewWorkspace: DASHBOARD_NEW_WORKSPACE_ROUTE,
  workspaceRoot: `/${WORKSPACE_ROOT_SEGMENT}/:${ROUTE_PARAMS.workspaceSlug}`,
  workspaceSection: `/${WORKSPACE_ROOT_SEGMENT}/:${ROUTE_PARAMS.workspaceSlug}/${WORKSPACE_SECTIONS.pos}`,
  workspaceOrders: `/${WORKSPACE_ROOT_SEGMENT}/:${ROUTE_PARAMS.workspaceSlug}/orders`,
  workspaceOrder: `/${WORKSPACE_ROOT_SEGMENT}/:${ROUTE_PARAMS.workspaceSlug}/orders/:${ROUTE_PARAMS.orderId}`,
  workspaceProducts: `/${WORKSPACE_ROOT_SEGMENT}/:${ROUTE_PARAMS.workspaceSlug}/products`,
  workspaceLogs: `/${WORKSPACE_ROOT_SEGMENT}/:${ROUTE_PARAMS.workspaceSlug}/logs`,
  workspaceUsers: `/${WORKSPACE_ROOT_SEGMENT}/:${ROUTE_PARAMS.workspaceSlug}/users`,
  workspaceSettings: `/${WORKSPACE_ROOT_SEGMENT}/:${ROUTE_PARAMS.workspaceSlug}/settings`,
  workspaceInvoicePrint: `/${WORKSPACE_ROOT_SEGMENT}/:${ROUTE_PARAMS.workspaceSlug}/invoices/:${ROUTE_PARAMS.invoiceId}/print`,
  superboard: `${SUPERBOARD_ROUTE}/*`,
  superboardLogin: SUPERBOARD_LOGIN_ROUTE,
  notFound: "*",
} as const;

export const isLocale = (value: string): value is Locale =>
  value === LOCALES.en || value === LOCALES.id;

export const PUBLIC_RESERVED_SEGMENTS: readonly string[] = [
  "login",
  "dashboard",
  WORKSPACE_ROOT_SEGMENT,
  SUPERBOARD_ROOT_SEGMENT,
  "api",
  "assets",
  "og",
];

/** Extracts the locale from a `/xx/...` pathname, if present. */
export const localeFromPathname = (pathname: string): Locale | null => {
  const [, first] = pathname.split("/");
  return first && isLocale(first) ? first : null;
};

/**
 * Swaps the locale prefix of a localized pathname, preserving the rest of the path.
 *
 * Returns `null` for non-localized routes (dashboard, superboard), which callers use
 * to skip navigation — the language switch must not bounce a user out of a workspace.
 *
 * `swapLocaleInPath("/en/contact", "id") // "/id/contact"`
 * `swapLocaleInPath("/dashboard", "id")  // null`
 */
export const swapLocaleInPath = (pathname: string, nextLocale: Locale): string | null => {
  const current = localeFromPathname(pathname);
  if (!current) return null;

  const rest = pathname.slice(current.length + 1);
  return `${publicLocalePath(nextLocale)}${rest}`;
};
